import { DPR, TYPES } from '../../../render/constants';
import { sleep, blobToDataURL } from '../../../common-utils';
import { createPNGStreamEncoder } from '../../../lib/encoder/png';
import { CacheProvider } from '../../../provider/cache';
import { CanvasProvider } from '../../../provider/canvas';
import { loadImageCache, getNodeImageCache } from '../../../provider/image';
import { renderNode } from '../../../render/node';
import { isURLFill } from '../../../render/utils/fill';

// Check if dimensions exceed canvas limits
export const maxCanvasSize = 16384; // Most browsers support 16384x16384 max

function isImageFilledNode(node) {
  if (node.type === TYPES.image) return true;
  if (node.type === TYPES.frame || node.type === TYPES.shape) {
    return isURLFill(node.fill);
  }
  return false;
}

export async function waitForImages(context, frames) {
  // Support both single frame and array of frames
  const frameArray = Array.isArray(frames) ? frames : [frames];
  const imageNodes = frameArray.flatMap((frame) => [
    frame,
    ...context.api.getDescendants(frame.id).filter(isImageFilledNode),
  ]);

  await Promise.all(
    imageNodes.map(async (node) => {
      try {
        loadImageCache(context, node);
        const imgCache = getNodeImageCache(context, node.id);
        if (!imgCache) return;
        await imgCache?.ready;
      } catch (error) {
        console.warn('Failed to load image for node:', node.id, error);
        // Continue even if individual image fails to load
      }
    })
  );
}

export async function renderNodes(
  renderContext,
  { nodes, exportMode = '', contentType = 'image/png', dpr = 2, quality = 1.0 }
) {
  await waitForImages(renderContext, nodes);
  // Use the same region logic as HTML/PPT export so that transforms (rotation, scale, etc.)
  // are correctly taken into account. getNodesRegion already returns a Rect instance.
  let rect = renderContext.api.getNodesRegion(nodes);
  if (!rect || !rect.w || !rect.h) {
    // Nothing visible – return a transparent 1x1 PNG to avoid downstream errors
    const emptyCanvas = document.createElement('canvas');
    emptyCanvas.width = 1;
    emptyCanvas.height = 1;
    const emptyBlob = await new Promise((resolve) => {
      emptyCanvas.toBlob((blob) => resolve(blob), 'image/png', 1);
    });
    return await blobToDataURL(emptyBlob);
  }

  try {
    const parent = renderContext.api.getNode(nodes[0].parentId);
    if (parent) {
      const parentRect = renderContext.api.getNodeRegion(parent).rect;
      if (parentRect) {
        rect.x = rect.x - parentRect.x;
        rect.y = rect.y - parentRect.y;
      }
    }
  } catch (e) {
    console.error(e);
  }

  const width = rect.w * dpr;
  const height = rect.h * dpr;

  let safeWidth, safeHeight;

  if (width > height) {
    // Landscape orientation
    safeWidth = Math.min(width, maxCanvasSize);
    safeHeight = Math.round((height / width) * safeWidth);
  } else {
    // Portrait or square orientation
    safeHeight = Math.min(height, maxCanvasSize);
    safeWidth = Math.round((width / height) * safeHeight);
  }

  // Ensure dimensions don't exceed maxCanvasSize in either direction
  if (safeHeight > maxCanvasSize) {
    safeHeight = maxCanvasSize;
    safeWidth = Math.round((width / height) * safeHeight);
  }
  if (safeWidth > maxCanvasSize) {
    safeWidth = maxCanvasSize;
    safeHeight = Math.round((height / width) * safeWidth);
  }

  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = safeWidth;
  finalCanvas.height = safeHeight;
  const finalContext = finalCanvas.getContext('2d', { willReadFrequently: true });

  // Calculate scaling factor for maintaining aspect ratio
  const scaleX = safeWidth / width;
  const scaleY = safeHeight / height;
  const scale = Math.min(scaleX, scaleY);

  // Create a single export context for the entire canvas
  const exportContext = Object.assign({}, renderContext, {
    context: finalContext,
    dpr: dpr * DPR * scale,
    mode: 'export',
    exportMode: exportMode,
    isNodeInViewport: null,
    providers: {
      ...renderContext.providers,
      cache: new CacheProvider(),
      canvas: new CanvasProvider(),
      scale: { scale: 1, translateX: 0, translateY: 0 },
    },
  });
  exportContext.providers.cache.attach(exportContext);
  exportContext.providers.canvas.attach(exportContext);

  const scaleProvider = exportContext.providers.scale;
  scaleProvider.scale = dpr * scale;
  scaleProvider.translateX = -rect.x * scaleProvider.scale;
  scaleProvider.translateY = -rect.y * scaleProvider.scale;

  // Apply transformations to position the content correctly
  finalContext.scale(scaleProvider.scale, scaleProvider.scale);
  finalContext.translate(
    scaleProvider.translateX / scaleProvider.scale,
    scaleProvider.translateY / scaleProvider.scale
  );

  // Get the node to render
  nodes.map((node) => {
    renderNode(exportContext, node);
  });

  await sleep(100);

  exportContext.providers.cache.dispose();
  exportContext.providers.canvas.dispose();
  // Build Blob to allow metadata injection
  let blob = await new Promise((resolve) => {
    finalCanvas.toBlob((blob) => resolve(blob), contentType, quality);
  });
  try {
    if (contentType === 'image/png') {
      const { width: w, height: h } = finalCanvas;
      const pngEncoder = createPNGStreamEncoder(w, h);
      const ctx = finalContext;
      const imgData = ctx.getImageData(0, 0, w, h);
      const row = new Uint8Array(w * 4);
      for (let y = 0; y < h; y++) {
        const start = y * w * 4;
        row.set(imgData.data.subarray(start, start + w * 4));
        pngEncoder.writeRow(row);
      }
      blob = pngEncoder.end();
    }
  } catch (e) {
    console.error(e);
  }

  return await blobToDataURL(blob);
}
